'use client';

import type { ReactNode } from 'react';
import toast, { type Toast as RHTToastProps } from 'react-hot-toast';

import { cn } from '@/services/utils/classnames';

import { CloseIcon, ErrorIcon, SuccessIcon } from './alert-toast-icons';
import type { ToastVariant } from './notify.types';

type AlertToastProps = {
  rhtToastProps: RHTToastProps;
  variant: ToastVariant;
  content: ReactNode;
};

const VARIANT_ICON = {
  success: SuccessIcon,
  error: ErrorIcon,
} as const;

export const AlertToast = ({
  rhtToastProps,
  variant,
  content,
}: AlertToastProps) => {
  const Icon = VARIANT_ICON[variant];

  const handleCancelClick = () => {
    toast.dismiss(rhtToastProps.id);
  };

  return (
    <div
      {...rhtToastProps.ariaProps}
      className={cn(
        'flex items-start min-w-[300px] p-2 rounded-dpDefault bg-dp-white shadow-dpTooltip text-dp-blue-800 text-xs font-bold',
        rhtToastProps.visible
          ? 'animate-[toast-enter_200ms_ease-out]'
          : 'animate-[toast-leave_150ms_ease-in_forwards]'
      )}
    >
      <Icon className="w-[22px] h-[22px] mr-2 shrink-0" />
      <div className="flex-1">{content}</div>
      <button
        type="button"
        aria-label="Close notification"
        onClick={handleCancelClick}
        className="ml-2 flex h-4 w-4 shrink-0 items-center justify-center rounded-dpRound border-0 bg-transparent p-0 cursor-pointer hover:opacity-80"
      >
        <CloseIcon className="h-4 w-4" />
      </button>
    </div>
  );
};
